import {
  Box,
  ButtonGroup,
  Card,
  Collapse,
  Divider,
  Flex,
  HStack,
  Link,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  usePrefersReducedMotion,
  VStack,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import pick from "lodash/pick";
import { FormEvent, useEffect, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Link as RouterLink } from "react-router";

import { Restricted } from "@/app/components/Restricted";
import { ConfirmationDialog } from "@/common/components/ConfirmationDialog";
import FormNumberInput from "@/common/components/Form/FormNumberInput";
import FormReset from "@/common/components/Form/FormReset";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSelect from "@/common/components/Form/FormSelect";
import FormSubmit from "@/common/components/Form/FormSubmit";
import FormSwitch from "@/common/components/Form/FormSwitch";
import { IcoFeatureCleanup } from "@/common/components/Icons";
import SectionDescription from "@/common/components/SectionDescription";
import { SectionHeading } from "@/common/components/SectionHeading";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { LinearCleanupTemplateSelect } from "@/global-settings/components/LinearCleanupTemplateSelect";
import { LinearCleanupTrustedSourceWarning } from "@/global-settings/components/LinearCleanupTrustedSourceWarning";
import { useLinearCleanupIssueCandidateCount } from "@/global-settings/data/useLinearCleanupIssueCandidateCount";
import { usePatchOrgMutation } from "@/global-settings/data/usePatchOrgMutation";
import { useGithubConnection } from "@/integration/data/useGithubConnection";
import { useLinearArchiveGuideOptions } from "@/integration/data/useLinearArchiveGuideOptions";
import { useLinearConnection } from "@/integration/data/useLinearConnection";
import { useFlag } from "@reflag/react-sdk";
import {
  PatchOrgArgsSchema,
  PatchOrgArgsType,
} from "@reflag/shared/organizationAPI";
import { GlobalSettingsUrl } from "@reflag/shared/urls";

function useSettingsSectionForm(
  defaultValues: PatchOrgArgsType,
  prepareVariables?: (data: PatchOrgArgsType) => PatchOrgArgsType,
) {
  const { org } = useEnvironment();
  const toast = useToast();
  const form = useForm<PatchOrgArgsType>({
    resolver: zodResolver(PatchOrgArgsSchema),
    defaultValues,
  });
  const mutation = usePatchOrgMutation(org.id);
  const handleSubmit = useFormMutationSubmitHandler(
    form,
    mutation,
    (result) => {
      // Keep this form scoped to its section even though PATCH returns the full org.
      form.reset(pick(result, Object.keys(defaultValues)));
      toast({ title: "Settings saved", status: "success" });
    },
    { prepareVariables },
  );
  return { form, handleSubmit, mutation };
}

function SectionFormActions({
  isLoading = false,
  loadingText,
}: {
  isLoading?: boolean;
  loadingText?: string;
} = {}) {
  return (
    <>
      <FormRootError />
      <ButtonGroup>
        <FormSubmit isLoading={isLoading} loadingText={loadingText} />
        <FormReset />
      </ButtonGroup>
    </>
  );
}

export function ArchivingFlow() {
  const { org } = useEnvironment();
  const prefersReducedMotion = usePrefersReducedMotion();
  const linearEnableDialog = useDisclosure();
  const createIssuesForExistingStaleFlags = useRef(false);
  const { isEnabled: linearCleanupEnabled } = useFlag(
    "linear-cleanup-automation",
  );

  const checks = useSettingsSectionForm({
    archiveAfterLastCheckInDays: org.archiveAfterLastCheckInDays,
    archiveAfterRolloutInDays: org.archiveAfterRolloutInDays,
  });
  const defaults = useSettingsSectionForm({
    autoArchive: org.autoArchive,
  });
  const {
    form,
    handleSubmit,
    mutation: linearSettingsMutation,
  } = useSettingsSectionForm(
    {
      autoCreateLinearCleanupIssue: org.autoCreateLinearCleanupIssue,
      linearCleanupIssueTeamId: org.linearCleanupIssueTeamId,
      linearCleanupIssueTemplateId: org.linearCleanupIssueTemplateId,
    },
    (data) => {
      if (
        data.autoCreateLinearCleanupIssue &&
        linearTeams.length > 1 &&
        !data.linearCleanupIssueTeamId
      ) {
        throw new Error("Select a default Linear team");
      }
      if (data.autoCreateLinearCleanupIssue) {
        const template = linearArchiveGuideOptions?.templates?.find(
          (template) => template.id === data.linearCleanupIssueTemplateId,
        );
        if (!template || template.teamId) {
          throw new Error("Select an available workspace template");
        }
      }
      return createIssuesForExistingStaleFlags.current
        ? {
            ...data,
            createLinearCleanupIssuesForExistingStaleFlags: true,
          }
        : data;
    },
  );

  const { env } = useEnvironment();
  const {
    data: linearCleanupIssueCandidateCount,
    isFetching: linearCleanupIssueCandidateCountLoading,
    isError: linearCleanupIssueCandidateCountError,
    refetch: refetchLinearCleanupIssueCandidateCount,
  } = useLinearCleanupIssueCandidateCount(org.id, { enabled: false });

  const { data: githubConnection, isLoading: githubLoading } =
    useGithubConnection();
  const { data: linearConnection, isLoading: linearLoading } =
    useLinearConnection({ enabled: linearCleanupEnabled });
  const {
    data: linearArchiveGuideOptions,
    isPending: linearOptionsLoading,
    isError: linearOptionsError,
    refetch: refetchLinearOptions,
  } = useLinearArchiveGuideOptions({
    enabled:
      linearCleanupEnabled &&
      !!linearConnection &&
      !linearConnection.needsReauthorization,
  });

  useEffect(() => {
    const teams = linearArchiveGuideOptions?.teams ?? [];
    if (!teams.length) {
      return;
    }

    const selectedTeamId = form.getValues("linearCleanupIssueTeamId");

    if (teams.length === 1) {
      if (selectedTeamId !== teams[0].id) {
        form.setValue("linearCleanupIssueTeamId", teams[0].id, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
      return;
    }

    if (selectedTeamId && !teams.some((team) => team.id === selectedTeamId)) {
      form.setValue("linearCleanupIssueTeamId", null, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [form, linearArchiveGuideOptions?.teams]);

  if (githubLoading || (linearCleanupEnabled && linearLoading)) {
    return <Spinner />;
  }

  const linearTeams = linearArchiveGuideOptions?.teams ?? [];
  const linearAgents = linearArchiveGuideOptions?.agents ?? [];
  const selectedTemplateId = form.watch("linearCleanupIssueTemplateId");
  const templates = (linearArchiveGuideOptions?.templates ?? []).filter(
    (template) => !template.teamId,
  );
  const isLinearCleanupAvailable =
    !!linearConnection &&
    !linearConnection.needsReauthorization &&
    linearTeams.length > 0;
  const autoCreateLinearCleanupIssue = form.watch(
    "autoCreateLinearCleanupIssue",
  );
  const shouldShowTeamPicker =
    isLinearCleanupAvailable && linearTeams.length > 1;
  const candidateCount = linearCleanupIssueCandidateCount ?? 0;
  const hasCandidateCount =
    !linearCleanupIssueCandidateCountError &&
    linearCleanupIssueCandidateCount !== undefined;
  const candidateCountDescription = linearCleanupIssueCandidateCountLoading
    ? "Checking how many stale flags need a Linear cleanup issue…"
    : linearCleanupIssueCandidateCountError
      ? "We couldn't determine how many stale flags need a Linear cleanup issue. Would you still like Reflag to create issues for every eligible flag?"
      : `${candidateCount} stale ${candidateCount === 1 ? "flag doesn't" : "flags don't"} already have a Linear cleanup issue.`;
  const createIssuesLabel = hasCandidateCount
    ? `Save and create ${candidateCount} ${candidateCount === 1 ? "issue" : "issues"}`
    : "Save and create issues";

  async function offerIssuesForExistingStaleFlags() {
    const result = await refetchLinearCleanupIssueCandidateCount();
    if (!result.isError && result.data === 0) {
      saveLinearSettings(false);
      return;
    }
    linearEnableDialog.onOpen();
  }

  function handleLinearSettingsSubmit(event: FormEvent<HTMLDivElement>) {
    if (
      !org.autoCreateLinearCleanupIssue &&
      form.getValues("autoCreateLinearCleanupIssue")
    ) {
      event.preventDefault();
      void offerIssuesForExistingStaleFlags();
      return;
    }

    createIssuesForExistingStaleFlags.current = false;
    void handleSubmit(event);
  }

  function saveLinearSettings(createExistingIssues: boolean) {
    createIssuesForExistingStaleFlags.current = createExistingIssues;
    void handleSubmit().finally(() => {
      createIssuesForExistingStaleFlags.current = false;
      linearEnableDialog.onClose();
    });
  }

  return (
    <Flex direction="column" gap={6} maxW="mediumForm">
      <Restricted alertStyle="callout" role="admin">
        <VStack align="flex-start" spacing={8}>
          <FormProvider {...checks.form}>
            <Flex
              as="form"
              direction="column"
              gap={3}
              w="full"
              onSubmit={checks.handleSubmit}
            >
              <SectionHeading fontSize="lg" fontWeight="semibold">
                Archive checks
              </SectionHeading>
              <SectionDescription>
                Reflag identifies stale flags and helps you safely archive them
                after they have served their purpose. Archival is only suggested
                when the 3 checks below pass.
              </SectionDescription>
              <Card align="flex-start" gap={4} p={5} variant="outline">
                <VStack align="flex-start" spacing={2}>
                  <HStack>
                    <Text fontSize="md" fontWeight="medium">
                      Stale
                    </Text>
                    <IcoFeatureCleanup />
                  </HStack>
                  <HStack fontSize="sm">
                    <Text color="dimmed">Pass check</Text>
                    <FormNumberInput
                      _control={{ w: "100px" }}
                      min={0}
                      name="archiveAfterRolloutInDays"
                      size="sm"
                      unit="days"
                    />
                    <Text color="dimmed">after rollout to everyone</Text>
                  </HStack>
                </VStack>
                <Divider />
                <VStack align="flex-start" spacing={2}>
                  <Text fontSize="md" fontWeight="medium">
                    Remove flag code
                  </Text>
                  {githubConnection?.repository ? (
                    <Text fontSize="sm">
                      <Text as="span" color="dimmed">
                        Pass check when there&apos;s no code references found in
                        the{" "}
                      </Text>
                      <Link
                        as={RouterLink}
                        to={GlobalSettingsUrl(env, "org-integrations")}
                      >
                        {githubConnection?.repository}
                      </Link>{" "}
                      <Text as="span" color="dimmed">
                        repo
                      </Text>
                    </Text>
                  ) : (
                    <Text fontSize="sm">
                      <Link
                        as={RouterLink}
                        to={GlobalSettingsUrl(env, "org-integrations")}
                      >
                        Connect with Github
                      </Link>{" "}
                      <Text as="span" color="dimmed">
                        to automatically create cleanup PRs using AI and check
                        for code references
                      </Text>
                    </Text>
                  )}
                </VStack>
                <Divider />
                <VStack align="flex-start" spacing={2}>
                  <Text fontSize="md" fontWeight="medium">
                    No access check last{" "}
                    {checks.form.watch("archiveAfterLastCheckInDays")} days
                  </Text>
                  <HStack fontSize="sm">
                    <Text color="dimmed">Pass check</Text>
                    <FormNumberInput
                      _control={{ w: "100px" }}
                      min={0}
                      name="archiveAfterLastCheckInDays"
                      size="sm"
                      unit="days"
                    />
                    <Text color="dimmed">after last access check</Text>
                  </HStack>
                </VStack>
              </Card>
              <SectionFormActions />
            </Flex>
          </FormProvider>
          {linearCleanupEnabled && (
            <FormProvider {...form}>
              <Flex
                as="form"
                direction="column"
                gap={3}
                w="full"
                onSubmit={handleLinearSettingsSubmit}
              >
                <SectionHeading fontSize="lg" fontWeight="semibold">
                  Linear cleanup
                </SectionHeading>
                <Box>
                  <Flex direction="column" gap={1}>
                    <Tooltip
                      isDisabled={isLinearCleanupAvailable}
                      label={
                        linearConnection?.needsReauthorization
                          ? "Reauthorize Linear to enable"
                          : linearConnection
                            ? "No accessible Linear teams found"
                            : "Connect Linear to enable"
                      }
                      offset={[0, 15]}
                      placement="right"
                      hasArrow
                    >
                      <Box w="full">
                        <FormSwitch
                          isDisabled={
                            !isLinearCleanupAvailable &&
                            !autoCreateLinearCleanupIssue
                          }
                          label="Create issues when flags become stale"
                          name="autoCreateLinearCleanupIssue"
                        />
                      </Box>
                    </Tooltip>
                    {(!linearConnection ||
                      linearConnection.needsReauthorization) && (
                      <Text fontSize="sm">
                        <Link
                          as={RouterLink}
                          to={GlobalSettingsUrl(env, "org-integrations")}
                        >
                          {linearConnection ? "Reauthorize" : "Connect"} Linear
                        </Link>{" "}
                        <Text as="span" color="dimmed">
                          to enable automation.
                        </Text>
                      </Text>
                    )}
                  </Flex>
                  {linearConnection &&
                    !linearConnection.needsReauthorization &&
                    !linearOptionsLoading &&
                    !linearOptionsError &&
                    linearArchiveGuideOptions?.codingSessionTrustedSource && (
                      <LinearCleanupTrustedSourceWarning
                        status={
                          linearArchiveGuideOptions.codingSessionTrustedSource
                        }
                        workspaceUrlKey={
                          linearArchiveGuideOptions.workspaceUrlKey
                        }
                      />
                    )}
                  <Collapse
                    in={!!autoCreateLinearCleanupIssue}
                    animateOpacity
                    transition={{
                      enter: { duration: prefersReducedMotion ? 0 : 0.2 },
                      exit: { duration: prefersReducedMotion ? 0 : 0.2 },
                    }}
                    unmountOnExit
                  >
                    <VStack align="flex-start" pt={5} spacing={4}>
                      {shouldShowTeamPicker && (
                        <FormSelect
                          label="Default team"
                          description="If the flag is already linked to a Linear issue, its team is used instead."
                          name="linearCleanupIssueTeamId"
                          w="50%"
                          options={linearTeams.map((team) => ({
                            label: team.name,
                            value: team.id,
                          }))}
                          placeholder="Select a team"
                        />
                      )}
                      <LinearCleanupTemplateSelect
                        isConnected={
                          !!linearConnection &&
                          !linearConnection.needsReauthorization
                        }
                        isLoading={linearOptionsLoading}
                        isError={linearOptionsError}
                        showUnavailableWarning={
                          org.autoCreateLinearCleanupIssue
                        }
                        onRetry={() => void refetchLinearOptions()}
                        value={selectedTemplateId ?? null}
                        onChange={(value) =>
                          form.setValue("linearCleanupIssueTemplateId", value, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        }
                        templates={templates}
                        agents={linearAgents}
                        workspaceUrlKey={
                          linearArchiveGuideOptions?.workspaceUrlKey
                        }
                      />
                    </VStack>
                  </Collapse>
                </Box>
                <SectionFormActions
                  isLoading={linearCleanupIssueCandidateCountLoading}
                  loadingText="Counting flags"
                />
              </Flex>
              <ConfirmationDialog
                isOpen={linearEnableDialog.isOpen}
                onClose={linearEnableDialog.onClose}
                onCancel={() => saveLinearSettings(false)}
                onConfirm={() => saveLinearSettings(true)}
                isLoading={
                  linearSettingsMutation.isPending ||
                  linearCleanupIssueCandidateCountLoading
                }
                title="Include existing stale flags?"
                description={candidateCountDescription}
                showCloseAsButton
                closeLabel="Cancel"
                cancelLabel="Save"
                confirmLabel={createIssuesLabel}
                loadingText={
                  linearCleanupIssueCandidateCountLoading
                    ? "Counting flags"
                    : "Saving"
                }
              />
            </FormProvider>
          )}
          <FormProvider {...defaults.form}>
            <Flex
              as="form"
              direction="column"
              gap={3}
              w="full"
              onSubmit={defaults.handleSubmit}
            >
              <SectionHeading fontSize="lg" fontWeight="semibold">
                Automation defaults for new flags
              </SectionHeading>
              <Card gap={5} p={5} variant="outline">
                <Flex direction="column" gap={1}>
                  <FormSwitch label="Auto-archive flag" name="autoArchive" />
                  <Text color="dimmed" fontSize="sm" maxW="sm">
                    Reflag will automatically archive the flag when all the
                    cleanup checks pass.
                  </Text>
                </Flex>
              </Card>
              <SectionFormActions />
            </Flex>
          </FormProvider>
        </VStack>
      </Restricted>
    </Flex>
  );
}
